import React from "react";
import { AppContext, User } from "../../../../context/appContext";
import CoachListProps from "../interface/coachListProps";
import CoachProps from "../interface/coachProps";
import axios from "axios";
import { endpoints } from "../../../../endpoints/endpoints";
import EditCoachProps from "../interface/editCoachProps";
import { enqueueSnackbar } from "notistack";

export const CoachListContext = React.createContext<any>({});

const CoachListProvider = ({ children }: any): React.JSX.Element => {
  const {
    user,
    checkSession,
  }: {
    user: User;
    checkSession: (response: any) => void;
  } = React.useContext(AppContext);

  const [coachList, setCoachList] = React.useState<CoachListProps>({
    loading: false,
    fetched: false,
    data: [] as CoachProps[],
  });

  const [editCoach, setEditCoach] = React.useState<EditCoachProps>({
    showDialog: false,
    coach: {} as CoachProps,
  });

  const [search, setSearch] = React.useState<string>("");
  const [addCoach, setAddCoach] = React.useState<boolean>(false);

  const fetchCoachList = React.useCallback(async (): Promise<void> => {
    setCoachList({
      ...coachList,
      loading: true,
    });

    await axios
      .post(endpoints.admin.getCoachListObject, {
        sessionId: user.sessionId,
      })
      .then(async (response) => {
        checkSession(response);

        // add expand property to each coach
        response.data.forEach((coach: CoachProps) => {
          coach.expand = false;
        });

        setCoachList({
          loading: false,
          fetched: true,
          data: response.data,
        });
      })
      .catch((error) => {
        console.error(error);
      });
  }, [checkSession, coachList, user.sessionId]);

  const doEdit = React.useCallback(async (): Promise<void> => {
    await axios
      .post(endpoints.admin.editCoach, {
        sessionId: user.sessionId,
        coach: editCoach.coach,
      })
      .then(async (response) => {
        checkSession(response);

        if (response.data) {
          setEditCoach({
            ...editCoach,
            showDialog: false,
          });

          fetchCoachList();

          enqueueSnackbar("Coach erfolgreich bearbeitet!", {
            variant: "success",
          });
        } else {
          enqueueSnackbar("Fehler beim Bearbeiten des Coaches!", {
            variant: "error",
          });
        }
      })
      .catch((error) => {
        console.error(error);
        enqueueSnackbar("Fehler beim Bearbeiten des Coaches!", {
          variant: "error",
        });
      });
  }, [checkSession, editCoach, fetchCoachList, user.sessionId]);

  React.useEffect(() => {
    if (coachList.fetched) return;
    if (coachList.loading) return;
    if (coachList.data.length > 0) return;

    fetchCoachList();
  }, [coachList, fetchCoachList]);

  const value: {
    coachList: CoachListProps;
    setCoachList: (coachList: CoachListProps) => void;
    editCoach: EditCoachProps;
    setEditCoach: (editCoach: EditCoachProps) => void;
    search: string;
    setSearch: (search: string) => void;
    addCoach: boolean;
    setAddCoach: (addCoach: boolean) => void;
    fetchCoachList: () => void;
    doEdit: () => void;
  } = React.useMemo(
    () => ({
      coachList,
      setCoachList,
      editCoach,
      setEditCoach,
      search,
      setSearch,
      addCoach,
      setAddCoach,
      fetchCoachList,
      doEdit,
    }),
    [addCoach, coachList, editCoach, fetchCoachList, search, doEdit]
  );

  return (
    <CoachListContext.Provider value={value}>
      {children}
    </CoachListContext.Provider>
  );
};

export default CoachListProvider;
